import React, { useState } from 'react';
import PricingSummaryCalculator from '../../../../Domain/PricingSummaryCalculator';
import FigmaButton from '../../../../Elements/FigmaButton';
import ButtonColorsEnum from '../../../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../../../Enums/IconPositionEnum';
import ErrorMessage from '../../../../Elements/ErrorMessage';

/**
 * StatusBarCardV2 — Sigma button + popup showing corrected pricing summary (Adjusted Pricing Summary).
 *
 * Positioned bottom-right via .button-container CSS. Replaces StatusBarCardOverallTotals.
 * Click to pin the popup open; hover to preview.
 *
 * Props:
 *   Tier          — TierItemList (same object as StatusBarCard; we read .TierList)
 *   pricingDetail — VPricingDetailBaseItem (live-edited values)
 *   UserCanMarkup — boolean (true when OO ≠ FW office)
 *   OverallTier   — TierItemListForOverall (overall tiers)
 */
const StatusBarCardV2 = ({ Tier, pricingDetail, UserCanMarkup, OverallTier }) => {
  const [showInfo, setShowInfo] = useState(false);

  // Compute overall (cross-pricing) display values using corrected calculator.
  // Recompute on every render (not memoized) because the parent class component
  // mutates OverallTier/pricingDetail in-place, so reference equality can't
  // detect changes.  The calculation is lightweight pure math.
  let overallValues = null;
  try {
    if (OverallTier && OverallTier.TierList && OverallTier.TierList.length > 0 && pricingDetail) {
      const tiersByPd = new Map();
      for (const tier of OverallTier.TierList) {
        const pd = tier.VPricingDetailBaseItem;
        if (!pd) continue;
        const key = pd.PricingDetailId;
        if (!tiersByPd.has(key)) {
          // For the current pricing use the live-edited pricingDetail prop
          // instead of the stale VPricingDetailBaseItem snapshot from the API,
          // so edits to pricing-detail fields (misc costs, employee costs, etc.)
          // are reflected immediately without saving.
          const livePd = key === pricingDetail.PricingDetailId ? pricingDetail : pd;
          tiersByPd.set(key, { tiers: [], pd: livePd });
        }
        tiersByPd.get(key).tiers.push(tier);
      }

      const pricings = Array.from(tiersByPd.values());
      if (pricings.length > 0) {
        overallValues = PricingSummaryCalculator.computeOverallTotals(pricings, pricingDetail);
      }
    }
  } catch (e) {
    ErrorMessage.SetWarning(`Error computing overall totals, please inform IT!`);
  }

  const isSameOffice = overallValues?.isSameOffice ?? true;

  return (
    <div className={`button-container${!UserCanMarkup ? ' button-container-single-row' : ''}`}>
      <FigmaButton
        className='static-middle-right'
        type={ButtonTypeEnum.LargeIconOnly}
        color={showInfo ? ButtonColorsEnum.Secondary : ButtonColorsEnum.Primary}
        iconName=' fa-solid fa-sigma'
        disabled={!overallValues}
        onClick={() => setShowInfo(!showInfo)}
        iconPosition={IconPositionEnum.Center}
        tooltip='Show Overall Totals (Click to Keep Open)'
      />
      <div className={showInfo ? 'info-box' : 'info-box info-box-hidden'}>
        <div className='pd-rollup-head'>
          <div className='eyebrow'>Opportunity Roll-up</div>
          <h4>All Pricing Details</h4>
        </div>
        <div className='pd-rollup-group'>All Pricing Details</div>
        <div className='pd-rollup-row'>
          <span className='rl'>Total of all Revenue</span>
          <span className='rv'>
            <span className='a'>{overallValues?.revenueTotalFormatted ?? 'Calculating...'}</span>
          </span>
        </div>
        <div className='pd-rollup-row'>
          <span className='rl'>Total of all Costs</span>
          <span className='rv'>
            <span className='a'>{overallValues?.costTotalFormatted ?? 'Calculating...'}</span>
            <span className='p'>{(overallValues?.costPct ?? '') + ' of revenue'}</span>
          </span>
        </div>
        <div className={'pd-rollup-row net' + ((overallValues?.netRevenueTotal ?? 0) < 0 ? ' neg' : '')}>
          <span className='rl'>Net of all Revenue</span>
          <span className='rv'>
            <span className='a'>{overallValues?.netRevenueTotalFormatted ?? 'Calculating...'}</span>
            <span className='p'>{(overallValues?.revenuePct ?? '') + ' margin'}</span>
          </span>
        </div>
        {!isSameOffice && (
          <>
            <div className='pd-rollup-group'>Fieldwork Only</div>
            <div className='pd-rollup-row'>
              <span className='rl'>Total of all FW Revenue</span>
              <span className='rv'>
                <span className='a'>{overallValues?.fwRevenueTotalFormatted ?? 'Calculating...'}</span>
              </span>
            </div>
            <div className='pd-rollup-row'>
              <span className='rl'>Total of all FW Costs</span>
              <span className='rv'>
                <span className='a'>{overallValues?.fwCostTotalFormatted ?? 'Calculating...'}</span>
                <span className='p'>{(overallValues?.fwCostPct ?? '') + ' of revenue'}</span>
              </span>
            </div>
            <div className={'pd-rollup-row net' + ((overallValues?.fwNetRevenueTotal ?? 0) < 0 ? ' neg' : '')}>
              <span className='rl'>Net of all FW Revenue</span>
              <span className='rv'>
                <span className='a'>{overallValues?.fwNetRevenueTotalFormatted ?? 'Calculating...'}</span>
                <span className='p'>{(overallValues?.fwNetRevenuePct ?? '') + ' margin'}</span>
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StatusBarCardV2;
